import scrollToBottom from "./scrollBottom";
import { marked } from 'marked';

// Safe markdown parsing function
function safeMarkdownParse(text) {
  try {
    // Check if the text contains any markdown-like elements
    const hasMarkdown = /([*_~`#\[\]()>]|\d+\.)/.test(text);
    
    // If no markdown detected, return the original text
    if (!hasMarkdown) {
      return text;
    }

    // Attempt to parse markdown
    const parsed = marked.parse(text, {
      breaks: true,     // Convert line breaks to <br>
      gfm: true,        // GitHub Flavored Markdown
      headerIds: false, // Disable header IDs
      sanitize: true    // Sanitize HTML to prevent XSS
    });

    // Additional safety check
    return parsed.trim() ? parsed : text;
  } catch (error) {
    console.warn('Markdown parsing error:', error);
    return text; // Fallback to original text if parsing fails
  }
}

function formatMessageDateTime() {
  const now = new Date();
  const time = now.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });

  return time;
}

class processRequest {
  constructor() {
    this.shadowRoot = window.shadowDOM.shadowRoot;
    this.isLoading = false;
    this.init();
  }

  init() {
    try {
      const form = this.shadowRoot.querySelector("#anyForm_bot");
      const textarea = form.querySelector('textarea[name="prompt"]');
      const inputBox = textarea.closest('.inputBox');
      const containner = this.shadowRoot.querySelector(".any_botContent");
  
      if (!form || !textarea || !containner) {
        console.error('Form, textarea, or container not found in Shadow DOM');
        return;
      }
  
      textarea.addEventListener('input', () => {
        // Reset height to auto to calculate the correct scroll height
        textarea.style.height = 'auto';
        
        // Calculate scroll height
        const scrollHeight = textarea.scrollHeight;
        
        // Limit height to 80px
        const newHeight = Math.min(scrollHeight, 55);
        textarea.style.height = `${newHeight}px`;
        
        // Handle overflow
        textarea.style.overflowY = scrollHeight > 55 ? 'auto' : 'hidden';
      });
  
      // Handle Enter key for submission
      textarea.addEventListener('keydown', (e) => {
        // Submit on Enter without Shift key
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          form.dispatchEvent(new Event('submit'));
        }
        // Allow Shift+Enter for new line
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault();
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const value = textarea.value;
          
          // Insert new line at cursor position
          textarea.value = value.substring(0, start) + '\n' + value.substring(end);
          
          // Move cursor to the new line
          textarea.selectionStart = textarea.selectionEnd = start + 1;
        }
      });
  
      let conversations = [];
  
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        
        if (this.isLoading) {
          return; // Prevent multiple submissions while loading
        }
  
        const getPrompt = textarea.value.trim();
        const nonce = form.querySelector('input[name="any_bot_nonce"]').value;
  
        if (!getPrompt || !nonce) {
          console.error('Prompt or nonce missing');
          return;
        }
  
        // Reset textarea and input box height to original state
        textarea.style.height = '35px';
        inputBox.style.height = '55px';
  
        // Disable input while processing
        textarea.disabled = true;
        const submitButton = form.querySelector('button[type="submit"]');
        submitButton.disabled = true;
  
        conversations.push({ role: "tu", body: getPrompt });
        textarea.value = ''; // Clear textarea
        this.render(conversations, containner);
  
        // Add loader to chat flow
        const loaderHtml = `<div id="temp-loader" class="chat-loader">
            <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
            </div>
        </div>`;
        containner.innerHTML += loaderHtml;
        scrollToBottom(containner);
        
        this.isLoading = true;
  
        try {
          const response = await this.makeApiRequest(getPrompt, nonce);
          console.log('Raw response:', response);
          let parsedResponse;
          
          try {
            parsedResponse = JSON.parse(response);
            console.log('Parsed response:', parsedResponse);
          } catch (parseError) {
            console.error('JSON parsing error:', parseError);
            throw new Error('Failed to parse response');
          }
  
          // Handle 500 error response
          if (parsedResponse.statusCode === 500) {
            conversations.push({ 
              role: 'system', 
              body: 'Lo siento, ha ocurrido un error. Por favor, espera un momento antes de enviar tu próximo mensaje.' 
            });
            throw new Error('Server error: ' + parsedResponse.message);
          }
  
          // For success response
          if (typeof parsedResponse === 'string') {
            try {
              parsedResponse = JSON.parse(parsedResponse);
            } catch (e) {
              console.error('Error parsing nested JSON:', e);
            }
          }
  
          // Final validation
          if (parsedResponse && parsedResponse.role && parsedResponse.body) {
            const { body, role, conversationId } = parsedResponse;
            conversations.push({ role, body });
            
            if (conversationId) {
              this.confirmCookie(conversationId);
            }
          } else {
            throw new Error('Invalid response format');
          }
  
        } catch (error) {
          console.error('API Request failed:', error);
          // Show error message in chat
          conversations.push({ 
            role: 'system', 
            body: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, inténtalo de nuevo.' 
          });
        } finally {
          // Always cleanup regardless of success/failure
          this.isLoading = false;
          const tempLoader = containner.querySelector('#temp-loader');
          if (tempLoader) {
            tempLoader.remove();
          }
          
          // Re-enable input
          textarea.disabled = false;
          submitButton.disabled = false;
          
          // Render final state
          this.render(conversations, containner);
          scrollToBottom(containner);
        }
      });
    } catch (error) {
      console.error('Chat initialization failed:', error);
    }
  }

  async makeApiRequest(prompt, nonce) {
    return new Promise((resolve, reject) => {
      jQuery.ajax({
        type: "POST",
        url: anyBacken.ajaxurl,
        dataType: "text",
        timeout: 300000, // 5 minutes timeout
        data: {
          action: "botActions",
          prompt: prompt,
          nonce: nonce
        },
        success: function(response) {
          console.log('Raw response:', response);
          resolve(response);
        },
        error: function(jqXHR, textStatus, errorThrown) {
          console.error('AJAX error:', textStatus, errorThrown);
          reject(new Error(errorThrown || textStatus));
        }
      });
    });
  }

  render(conversations, containner) {
    const { role, body } = conversations[conversations.length - 1];
    const timeString = formatMessageDateTime();
    
    // Use safe markdown parsing
    const renderedBody = role != "tu" 
      ? safeMarkdownParse(body) 
      : body.replace(/\n/g, '<br>');
    
    containner.innerHTML += `<div class=${
        role != "tu" ? "bublesAi" : "bublesYou"
    }><div class="any_subjectBoxWrapper"><div class="any_subjectBox">
        ${
            role != "tu"
            ? `<img style="background-color:#${anyBacken.primaryColor};" src="${anyBacken.botIcon}" width="15px" height="15px" alt="botIcon">`
            : ""
        }  
        <span class="any_botName">${
            role != "tu"
            ? anyBacken.sparkName.replace(/(?:\-)/g, " ")
            : "Tú"
        }</span> </div> 
        <span style="background-color:${
            role != "tu" ? "#ffffff" : "#" + anyBacken.secondaryColor
        };">${renderedBody}</span>
        <small class="message-time">${timeString}</small>
        </div></div>`;

    scrollToBottom(containner);
  }

  confirmCookie(id) {
    try {
      if (!id) return null;

      // Always set the cookie with new conversation ID
      let date = new Date();
      date.setTime(date.getTime() + 1 * 60 * 60 * 1000); // 1 hour
      document.cookie = `${anyBacken.historyCookie}=${id}; expires=${date}; path=/; Secure`;
      return id;
    } catch (error) {
      console.error('Cookie handling error:', error);
      return false;
    }
  }
}

export default processRequest;
